/* eslint-disable max-lines */

import {
  Box,
  Button,
  Divider,
  Flex,
  Link,
  Skeleton,
  Text,
} from '@joinrepublic/dls-ui-kit';
import { useRouter } from 'next/router';

import {
  IssuerProfileDropdown,
  UserDropdown,
} from '@r-client/republic/feature/layout';
import { NotificationsDropdownMenuButton } from '@r-client/republic/feature/notifications';
import { useAnalytics } from '@r-client/shared/data/analytics';
import { MaybeValue, Viewer } from '@r-client/shared/data/auth';
import {
  E_US_ACCOUNT_ACCESS_STATE,
  useUsAccountAccessState,
} from '@r-client/shared/feature/global-auth';
import { GlobeIcon, PortfolioIcon } from '@r-client/shared/ui/chakra-ui-icons';
import { Avatar } from '@r-client/shared/ui/core';
import { Tooltip } from '@r-client/shared/ui/core';

import { HEADER_THEMES } from '../../types';
import { getOpenAccountPath } from '../../utils/get-open-account-path';
import { getSignInPath } from '../../utils/get-signin-path';
import { getTextColor } from '../../utils/get-text-color';
import { headerLinkStyles } from '../../utils/header-link-styles';
import { MessagesMenu } from '../messages-menu/messages-menu';
import { RegionSwitcherDesktop } from '../region-switcher/region-switcher-desktop';
import { IRegionSwitcherSettings } from '../region-switcher/types';

interface IUserMenuProps {
  theme: HEADER_THEMES;
  regionSwitcherSettings?: IRegionSwitcherSettings;
  user: {
    viewer: MaybeValue<Viewer>;
    isLoading?: boolean;
  };
  onNotificationsClick: () => void;
}

export const UserMenu = ({
  theme,
  user: { viewer, isLoading },
  onNotificationsClick,
  regionSwitcherSettings,
}: IUserMenuProps) => {
  const path = useRouter().pathname;
  const analytics = useAnalytics();

  const { state: accessState, isUsAuthenticated: showAuthenticatedMenu } =
    useUsAccountAccessState(!!viewer?.info);
  const showLoggedInMenu = accessState !== E_US_ACCOUNT_ACCESS_STATE.SIGNED_OUT;

  const editableOfferings = viewer?.info?.editableOfferings.filter((editable) =>
    viewer.info?.issuerProfiles.some(
      (profile) =>
        !profile.offerings.some((offering) => offering.id === editable.id)
    )
  );

  const profileSlug = viewer?.info?.slug;
  const profileHref = profileSlug ? `/@${profileSlug}` : '/register';
  const profileLinkLabel = profileSlug ? 'Your profile' : 'Create your profile';

  const trackPortfolioIconClick = () => {
    analytics.track({
      name: 'header_icon_clicked',
      params: { label: 'my_portfolio', platform: 'web' },
    });
  };

  return (
    <Flex>
      {isLoading && !viewer && (
        <Flex alignItems="center" gap="5">
          <Skeleton
            borderRadius="xl"
            startColor="grey.50"
            endColor="rgba(242,242,242, 0.5)"
            height={{ xs: '20px' }}
            width={{ xs: '60px', md: '60px' }}
          />
          <Skeleton
            borderRadius="base"
            startColor="grey.50"
            endColor="rgba(242,242,242, 0.5)"
            height={{ xs: '35px', md: '35px' }}
            width={{ xs: '35px', md: '35px' }}
          />
        </Flex>
      )}
      {!isLoading && (
        <Flex mr="5" alignItems="center" gap="5">
          {regionSwitcherSettings ? (
            <RegionSwitcherDesktop
              regionalSwitcherSettings={regionSwitcherSettings}
              variant={theme}
            />
          ) : (
            <GlobeButton
              tooltipText="You are viewing the global site"
              theme={theme}
            />
          )}
          <Box py="1.5" height="100%">
            <Divider
              orientation="vertical"
              borderColor="grey.100"
              opacity="1"
            />
          </Box>
        </Flex>
      )}
      {!isLoading && showAuthenticatedMenu && (
        <Flex mr="5" alignItems="center" gap="5">
          <MessagesMenu
            unreadMessages={viewer?.info?.unreadMessagesCount}
            theme={theme}
          />
          <NotificationsDropdownMenuButton
            color={getTextColor(theme)}
            unreadNotificationsCount={
              viewer?.info?.unreadNotificationsCount ?? 0
            }
            onNotificationsClick={onNotificationsClick}
          />
          <Tooltip content="My portfolio" delayHide={0} placement="bottom">
            {/*
              The icon is the link's only content, so without a name the link reads as empty
              (axe `link-name`, WCAG 2.4.4). The tooltip cannot supply it: it renders into a
              portal and is not referenced from the anchor.
            */}
            <Link
              {...headerLinkStyles}
              aria-label="My portfolio"
              href="/portfolio"
              onClick={trackPortfolioIconClick}
            >
              <PortfolioIcon boxSize="6" color={getTextColor(theme)} />
            </Link>
          </Tooltip>
        </Flex>
      )}
      {!isLoading && !showLoggedInMenu && (
        <Flex gap="6" flexDirection="row" alignItems="center">
          <Link
            {...headerLinkStyles}
            href={getSignInPath(path)}
            _hover={{
              textDecoration: 'none',
              color: 'blue.500',
            }}
          >
            <Text color={getTextColor(theme)}>Log in</Text>
          </Link>
          <Button
            as="a"
            href={getOpenAccountPath(regionSwitcherSettings?.activeVariantID)}
            size="default"
          >
            Open account
          </Button>
        </Flex>
      )}
      {!!viewer?.info?.issuerProfiles &&
        !!viewer.info.issuerProfiles.length && (
          <IssuerProfileDropdown
            url={viewer.info.issuerProfiles[0].offerings[0]?.logoUrl ?? ''}
            companyName={viewer.info.issuerProfiles[0].companyName || ''}
            onboardingCompleteness={
              viewer.info.issuerProfiles[0].onboardingCompleteness
            }
            offerings={viewer.info.issuerProfiles[0].offerings}
          />
        )}
      {showLoggedInMenu && (
        <UserDropdown
          userSlug={viewer?.info?.slug ?? ''}
          editableOfferings={editableOfferings}
          isFounder={!!viewer?.info?.issuerProfiles.length}
          triggerElement={
            /*
              The avatar is this link's only content. Its `<img>` had no `alt` (axe `image-alt`,
              WCAG 1.1.1) and the fallback is a `<title>`-less SVG, so the link was unnamed either
              way (axe `link-name`, WCAG 2.4.4). Name the link and mark the image decorative:
              labelling both would announce the same thing twice.
            */
            <Link
              aria-label={profileLinkLabel}
              href={profileHref}
              variant={undefined}
              size="republic-default"
            >
              <div data-testid="header-avatar">
                <Avatar
                  alt=""
                  outline={false}
                  url={viewer?.info?.avatarUrl ?? undefined}
                  size="xSmall"
                  badge={viewer?.info?.isAdmin ? 'admin' : undefined}
                />
              </div>
            </Link>
          }
        />
      )}
    </Flex>
  );
};

interface IGlobeIconProps {
  text?: string;
  tooltipText: string;
  theme: HEADER_THEMES;
}

function GlobeButton({ text, tooltipText, theme }: IGlobeIconProps) {
  return (
    <Tooltip content={tooltipText} delayHide={0} placement="bottom">
      <Flex
        _hover={theme !== 'dark' ? { bg: 'grey.50', color: 'grey.900' } : {}}
        borderRadius="lg"
        padding="2"
        color={getTextColor(theme)}
        cursor="default"
        alignItems="center"
      >
        <GlobeIcon boxSize="5" />
        {text && (
          <Text
            as="span"
            verticalAlign="middle"
            ml="1"
            size="body-sm"
            color={getTextColor(theme, '600')}
          >
            {text}
          </Text>
        )}
      </Flex>
    </Tooltip>
  );
}
